import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../../contexts/LanguageContext';
import NorwayMap from '../../../components/NorwayMap';

const STR = {
  no: {
    eyebrow: 'Norvig i Norge',
    titleA: 'Dekning som vokser,',
    titleB: 'kommune for kommune.',
    body: 'Hver prikk er en kommune med åpne stillinger på Norvig akkurat nå. Kartet oppdateres automatisk.',
    jobs: (n) => `${n} ${n === 1 ? 'åpen stilling' : 'åpne stillinger'}`,
    kommuner: (n) => `${n} ${n === 1 ? 'aktiv kommune' : 'aktive kommuner'}`,
    totalJobs: (n) => `${n} ${n === 1 ? 'åpen stilling' : 'åpne stillinger'} totalt`,
  },
  en: {
    eyebrow: 'Norvig across Norway',
    titleA: 'Coverage that grows,',
    titleB: 'municipality by municipality.',
    body: 'Each dot is a municipality with open positions on Norvig right now. The map updates automatically.',
    jobs: (n) => `${n} open position${n === 1 ? '' : 's'}`,
    kommuner: (n) => `${n} active municipalit${n === 1 ? 'y' : 'ies'}`,
    totalJobs: (n) => `${n} open position${n === 1 ? '' : 's'} in total`,
  },
};

const NorwayCoverage = () => {
  const { language } = useLanguage();
  const tx = STR[language === 'en' ? 'en' : 'no'];
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/municipalities/active`)
      .then((r) => setData(r.data))
      .catch(() => setData({ municipalities: [], total_open_jobs: 0 }));
  }, []);

  const rows = data?.municipalities || [];
  if (data && rows.length === 0) return null;

  return (
    <section className="border-t border-ink/[0.06] bg-paper" data-testid="landing-norway-coverage">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="font-sans text-xs uppercase tracking-widest text-ink/50">{tx.eyebrow}</p>
          <h2 className="mt-5 font-display font-light text-3xl md:text-4xl text-ink leading-tight">
            {tx.titleA}
            <span className="block font-serif italic text-fjord">{tx.titleB}</span>
          </h2>
          <p className="mt-5 font-sans text-base text-ink/60 leading-relaxed max-w-md">{tx.body}</p>
          {data && (
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <div className="font-display font-light text-4xl text-ink" data-testid="coverage-kommuner-count">{rows.length}</div>
                <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">{tx.kommuner(rows.length).replace(/^\d+ /, '')}</div>
              </div>
              <div>
                <div className="font-display font-light text-4xl text-ink" data-testid="coverage-jobs-count">{data.total_open_jobs}</div>
                <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">{tx.totalJobs(data.total_open_jobs).replace(/^\d+ /, '')}</div>
              </div>
            </div>
          )}
          <ul className="mt-8 space-y-1.5">
            {rows.slice(0, 5).map((m) => (
              <li key={m.kommune} className="font-sans text-sm text-ink/60">
                <span className="inline-block w-2 h-2 rounded-full bg-fjord mr-2 align-middle" />
                <span className="font-medium text-ink/80">{m.kommune}</span> — {tx.jobs(m.open_jobs)}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 max-w-md mx-auto w-full">
          <NorwayMap markers={rows} jobLabel={tx.jobs} accent="#2A5A4E" />
        </div>
      </div>
    </section>
  );
};

export default NorwayCoverage;
